import './lib/i18n'
import * as Sentry from '@sentry/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router'
import { MotionConfig } from 'framer-motion'
import { AuthProvider, isSessionExpiredError, setAuthTelemetry } from '@structural-climbing/auth'
import {
  UIProvider,
  installStaleChunkRecovery,
  installVersionCheck,
  registerServiceWorker,
  setPwaInstallTelemetry,
} from '@structural-climbing/ui'
import { App } from './App'
import { setupSentryFetch } from './lib/sentryFetch'
import './globals.css'

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: (import.meta.env.VITE_APP_ENV as string | undefined) ?? import.meta.env.MODE,
  enabled: import.meta.env.PROD,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 0.05,
  // A session that ended is not a crash. Auth0's silent-token call rejects with
  // login_required whenever the browser will not hand over the session cookie —
  // routine on iOS Safari, which evicts our refresh token after seven days and
  // blocks the iframe fallback as third-party. The apps handle it by sending the
  // member to sign in; reporting it as well buries real errors.
  beforeSend: (event, hint) => (isSessionExpiredError(hint?.originalException) ? null : event),
})

if (import.meta.env.PROD) {
  setupSentryFetch(import.meta.env.VITE_API_URL as string, Sentry.captureEvent)
  // The Worker reports its version on every response. When it is ahead of this
  // bundle the app shows a banner.
  installVersionCheck({ apiUrl: import.meta.env.VITE_API_URL as string, app: 'member' })
}

// Auth events that Sentry would otherwise never see. `beforeSend` above drops
// session-expired *exceptions* on purpose, and a failed silent-token call does
// not even reach it: the Auth0 SDK turns that rejection into a quiet
// unauthenticated state with no `error` set. These are captured as messages, so
// the filter keeps doing its job and the OAuth code still arrives.
setAuthTelemetry(({ name, data, level }) =>
  Sentry.captureMessage(name, { level: level ?? 'warning', extra: data }),
)

// A tap on Install that could not show the browser's prompt. The member still gets the
// manual steps, so nothing throws and Sentry would otherwise never hear about it.
setPwaInstallTelemetry(({ name, data }) =>
  Sentry.captureMessage(name, { level: 'warning', extra: data }),
)

installStaleChunkRecovery({
  onRecover: () => Sentry.captureMessage('Reloading after stale chunk import', 'info'),
})

registerServiceWorker()

const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN as string
const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID as string
const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE as string | undefined

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider domain={AUTH0_DOMAIN} clientId={AUTH0_CLIENT_ID} audience={AUTH0_AUDIENCE}>
        <UIProvider>
          {/* One switch for the whole app: everyone who has asked their OS for
              less motion gets none of it, without a guard in each component. */}
          <MotionConfig reducedMotion="user">
            <App />
          </MotionConfig>
        </UIProvider>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
)
